import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { AirspaceCheckerParametersWithStartEnd } from '@flyfreely-portal-ui/airspace';
import {
    AirspaceJurisdictionDto,
    FlyFreelyLoggingService,
    ImageReference,
    LocationDetailsDto,
    LocationDto,
    LocationFeatureDto,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { FullScreenService } from '@flyfreely-portal-ui/fullscreen';
import {
    FeatureAndGroup,
    FeatureGroup,
    MapDataInput,
    MapFeature,
    MapViewport
} from '@flyfreely-portal-ui/map';
import booleanValid from '@turf/boolean-valid';
import { getOrElse, map } from 'fp-ts/es6/Option';
import { pipe } from 'fp-ts/es6/function';
import {
    GeoJsonProperties,
    GeometryCollection,
    LineString,
    Point
} from 'geojson';
import { circleFeatureTypes } from 'libs/map/src/lib/constants';
import { buildAreaLineFeature } from 'libs/map/src/lib/draw-modes/draw-area-line';
import { buildCircleFeature } from 'libs/map/src/lib/draw-modes/draw-circle';
import { FlyFreelyMapComponent } from 'libs/map/src/lib/flyfreely-map/flyfreely-map.component';
import { MapMarkerToolbarComponent } from 'libs/map/src/lib/map-marker-toolbar/map-marker-toolbar.component';
import { Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { flightAreaOfFeatures, numerateNameIfExist } from '../helpers';
import { corridorFeatureTypes } from '../kml-upload.service';
import { MissionAirspaceParameters } from '../location-edit-v2/location-edit-dialogue.component';

// Mapbox requires a non-negative value for IDs
const NEW_FEATURE = 0;

@Component({
    selector: 'location-edit-drawing',
    templateUrl: './location-edit-drawing.component.html',
    styleUrls: ['./location-edit-drawing.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LocationEditDrawingComponent
    implements OnInit, OnChanges, OnDestroy
{
    /** The feature groups to display and edit. The component manages an internal
     * mutable copy; updates are emitted via featureGroupsChange. */
    @Input() featureGroups: FeatureGroup[];

    @Input() organisationId: number;
    @Input() showAirspaceChecker = false;
    @Input() type: LocationDetailsDto.Type | LocationDto.Type =
        LocationDto.Type.TEMPLATE;
    @Input() missionValues: MissionAirspaceParameters;
    @Input() workTracker: WorkTracker;
    /** Observable of the current airspace checker parameters */
    @Input() airspaceValue$: Observable<AirspaceCheckerParametersWithStartEnd>;
    /** Observable of external overlay data (LOTAM, MISSIONS, etc.) */
    @Input() mapData$: Observable<MapDataInput>;
    @Input() showNotices = true;
    @Input() showSiteBoundaries = true;
    @Input() showExternalMissions = true;
    @Input() currentTimeZone: string;
    @Input() additionalImages?: ImageReference[];
    /**
     * The minimum feature types required for the location to be valid.
     * If any of the types in the array are present, the location will be valid.
     * If an empty array is passed in, any feature will be valid.
     * Defaults to the minimum required types for location templates and mission locations.
     */
    @Input() requiredFeatureTypes: string[] = [
        LocationFeatureDto.Type.FLIGHT_AREA,
        LocationFeatureDto.Type.OFFSET_FLIGHT_AREA,
        LocationFeatureDto.Type.CIRCLE_FLIGHT_AREA
    ];
    @Input() mapJurisdiction: AirspaceJurisdictionDto;
    /** Controlled by parent (after checking preferences); shows the airspace disclaimer overlay */
    @Input() showAirspaceDisclaimer = false;

    // ---- Outputs that carry updated state back to the parent ----

    /** Emits full updated featureGroups array after any edit */
    @Output() featureGroupsChange = new EventEmitter<FeatureGroup[]>();
    @Output() hasRequiredFeaturesChange = new EventEmitter<boolean>();
    @Output() hasAnyFeaturesChange = new EventEmitter<boolean>();
    @Output() flightAreaChange = new EventEmitter<GeoJSON.Polygon | null>();

    // ---- Outputs that proxy map events the parent needs to react to ----

    @Output() geocodeResultSelected = new EventEmitter<MapFeature>();
    @Output() viewportChange = new EventEmitter<MapViewport>();
    @Output() jurisdictionChanged = new EventEmitter<AirspaceJurisdictionDto>();
    @Output() airspaceValuesUpdated =
        new EventEmitter<AirspaceCheckerParametersWithStartEnd>();
    @Output() airspaceDisclaimerAcknowledged = new EventEmitter<void>();
    @Output() editModeChanged = new EventEmitter<boolean | string>();
    @Output() isAddingFeatureChange = new EventEmitter<boolean>();
    @Output() isEditingChange = new EventEmitter<boolean>();

    // ---- ViewChildren ----

    @ViewChild('map', { static: false }) map: FlyFreelyMapComponent;
    @ViewChild('drawingArea', { static: true }) drawingArea: ElementRef;
    @ViewChild('markerToolbar') markerToolbar: MapMarkerToolbarComponent;

    // ---- Internal state ----

    /** Internal mutable copy of featureGroups */
    internalFeatureGroups: FeatureGroup[] = [];

    mapReady = false;
    private isLoaded = false;

    hoverFeatureId: number;
    editFeatureId: number;

    isAddingFeature = false;
    isEditing = false;

    /** Geometry type currently being drawn, used to offer the Backspace hint. */
    private addingFeatureType: FeatureGroup['type'] | null = null;

    selectedFeature: FeatureAndGroup;
    addedFeature: FeatureAndGroup;
    selectedFeatureGroup: FeatureGroup;

    invalidFeatures: Set<string> = new Set<string>();

    /** Lookup of `${featureGroupId}-${featureId}` -> tooltip text for the feature
     * name (name + category type), rebuilt whenever internalFeatureGroups changes.
     **/
    featureNameTooltips: { [key: string]: string } = {};

    /** Tracked internally for passing to the airspace checker sidebar */
    flightArea: GeoJSON.Polygon | null = null;

    private featuresUpdated$ = new Subject<boolean>();
    private ngUnsubscribe$ = new Subject<void>();
    /** Reference guard: tracks the last array emitted via featureGroupsChange so we
     * can ignore the echo when the parent passes it back through the [featureGroups] binding. */
    private _lastEmittedFeatureGroups: FeatureGroup[] | null = null;

    /** Set when a Line has just been converted to a corridor, so the corridor's
     * popup can offer a Cancel that reverts it back to the original Line.*/
    private pendingConversion: {
        sourceGroupId: number;
        originalFeature: MapFeature;
        convertedFeatureId: number | string;
    } | null = null;

    // ---- Undo history ----

    /** Stack of deep cloned snapshots, each one the state to restore to. */
    private undoStack: FeatureGroup[][] = [];
    private readonly maxUndoDepth = 50;
    /** States undone and not yet redone; any manual action clears it. */
    private redoStack: FeatureGroup[][] = [];
    /**
     * Snapshot taken at the start of the current action, held until that action
     * actually changes something.
     */
    private sessionBaseline: FeatureGroup[] | null = null;
    private sessionPushed = false;

    constructor(
        private fullscreenService: FullScreenService,
        private logging: FlyFreelyLoggingService,
        private changeDetector: ChangeDetectorRef
    ) {}

    ngOnInit() {
        this.featuresUpdated$
            .pipe(debounceTime(300), takeUntil(this.ngUnsubscribe$))
            .subscribe(updated => {
                if (!this.isLoaded) {
                    this.isLoaded = this.map.zoomToAllFeatures(updated);
                } else {
                    this.map.zoomToAllFeatures(updated);
                }
            });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['featureGroups']) {
            const incoming = this.featureGroups ?? [];
            // Skip the echo: when the parent stores our emitted array and passes it
            // back via the binding, the reference will be identical — ignore it.
            if (incoming === this._lastEmittedFeatureGroups) {
                return;
            }
            // Sync the internal copy whenever the parent provides a genuinely new set
            // (initial load, KML upload reset, etc.)
            this.setInternalFeatureGroups(incoming);
            this.pendingConversion = null;
            // The snapshots describe the previous set, so they can't be applied to
            // this one. Dropping them also keeps imports out of the undo history.
            this.clearUndoHistory();
            this.validateFeatures();
            this.refreshValidity();

            // If the map is already ready, zoom to show the newly loaded features
            if (!changes['featureGroups'].firstChange && this.mapReady) {
                this.featuresUpdated$.next(false);
            }
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.featuresUpdated$.complete();
        this.clearUndoHistory();
    }

    // ---- Keyboard ----

    @HostListener('document:keyup', ['$event'])
    handleDeleteKeyboardEvent(event: KeyboardEvent) {
        if (
            event.key === 'Delete' &&
            !this.isTextEntryFocused() &&
            // Mid-draw, Delete belongs to the map (remove the last point), and
            // selectedFeatureGroup is stale then and would delete the wrong thing
            !this.isAddingFeature &&
            this.selectedFeatureGroup &&
            this.selectedFeature
        ) {
            this.deleteFeature(
                this.selectedFeatureGroup,
                this.selectedFeature.feature
            );
        }
    }

    @HostListener('document:keydown', ['$event'])
    handleHistoryKeyboardEvent(event: KeyboardEvent) {
        const modifier = event.metaKey || event.ctrlKey;
        const key = event.key.toLowerCase();
        const isUndo = modifier && key === 'z' && !event.shiftKey;
        // Shift+Cmd/Ctrl+Z is the platform convention for redo and Ctrl+Y the
        // Windows one. Cmd+Y is left alone: it opens the browser history.
        const isRedo =
            (modifier && key === 'z' && event.shiftKey) ||
            (event.ctrlKey && !event.metaKey && key === 'y');
        if (!isUndo && !isRedo) {
            return;
        }
        // Typing in a field gets the browser's own text undo, not ours
        if (this.isTextEntryFocused() || this.isActionInProgress) {
            return;
        }
        event.preventDefault();
        if (isRedo) {
            this.redo();
        } else {
            this.undo();
        }
    }

    /** True when the keyboard focus is in a field the user types into. */
    private isTextEntryFocused(): boolean {
        const element = <HTMLElement>document.activeElement;
        return (
            element != null &&
            (['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName) ||
                element.isContentEditable)
        );
    }

    // ---- Public API for parent (accessed via @ViewChild) ----

    /** Call before closing the modal to cancel any in-progress edits. */
    handleEditOnClose() {
        if (this.isAddingFeature && this.selectedFeature != null) {
            this.cancelAddingFeature();
            // The draft is gone, so the add-finalize commit that doneEditing
            // triggers must not record the pre-add baseline as a step
            this.resetSession();
            this.map.doneEditing();
            this.onFeatureDelete(this.selectedFeature);
            this._setIsAddingFeature(false);
        }
        if (this.isEditing) {
            this.map.doneEditing();
        }
    }

    /** Returns the current internal feature groups (for use at save time). */
    getFeatureGroups(): FeatureGroup[] {
        return this.internalFeatureGroups;
    }

    /** Finish active map edit (called by parent before performing save). */
    doneEditing() {
        this.map.doneEditing();
    }

    /** Trigger zoom to all features (delegates to map). */
    zoomToAllFeatures(update?: boolean) {
        this.featuresUpdated$.next(update ?? false);
    }

    /**
     * Discard the undo history. Call this after assigning internalFeatureGroups
     * directly, since the existing snapshots describe the superseded set and
     * restoring one would drop the features that were just added.
     */
    clearUndoHistory() {
        this.undoStack = [];
        this.redoStack = [];
        this.resetSession();
    }

    // ---- Undo ----

    get canUndo(): boolean {
        return this.undoStack.length > 0;
    }

    get canRedo(): boolean {
        return this.redoStack.length > 0;
    }

    /**
     * The popup's buffer view previews geometry in place until Apply or
     * Cancel, so while it is open the feature is mid-action.
     */
    get isBufferPreviewOpen(): boolean {
        return this.markerToolbar?.popupView === 'buffer';
    }

    /**
     * Undo and redo work between actions, not during one, so both are held
     * off while a feature is being drawn or edited, or a buffer preview is open.
     */
    get isActionInProgress(): boolean {
        return (
            this.isEditing || this.isAddingFeature || this.isBufferPreviewOpen
        );
    }

    /**
     * A point is placed in one click and a circle in two, and neither draw mode
     * removes a vertex on Backspace, so only lines and polygons get the hint.
     */
    get canRemoveLastPoint(): boolean {
        return (
            this.addingFeatureType != null &&
            this.addingFeatureType !== 'Point' &&
            this.addingFeatureType !== 'Circle'
        );
    }

    /** Step back to the state before the last completed action. */
    undo() {
        if (this.isActionInProgress) {
            return;
        }

        const previous = this.undoStack.pop();
        if (previous == null) {
            return;
        }

        // The state being left is what redo brings back
        this.redoStack.push(
            this.cloneFeatureGroups(this.internalFeatureGroups)
        );
        this.restoreSnapshot(previous);
    }

    /** Reapply the most recently undone action. */
    redo() {
        if (this.isActionInProgress) {
            return;
        }

        const next = this.redoStack.pop();
        if (next == null) {
            return;
        }

        // pushUndo rather than pushSnapshot: redoing is not a manual action,
        // so it must not clear the rest of the redo stack
        this.pushUndo(this.cloneFeatureGroups(this.internalFeatureGroups));
        this.restoreSnapshot(next);
    }

    /** Make a snapshot the live state; the shared tail of undo and redo. */
    private restoreSnapshot(snapshot: FeatureGroup[]) {
        // Clone on the way in so nothing in either stack is reachable from the
        // live state, which later edits mutate in place.
        this.setInternalFeatureGroups(this.cloneFeatureGroups(snapshot));

        // The restored state may not contain the converted corridor, and a new
        // feature could later reuse its id, so the revert window closes here
        this.pendingConversion = null;
        this.setSelectedFeature(null);
        this.map.hidePopup();

        this.validateFeatures();
        this.refreshValidity();
        this.resetSession();

        this._lastEmittedFeatureGroups = this.internalFeatureGroups;
        this.featureGroupsChange.emit(this.internalFeatureGroups);
        this.changeDetector.detectChanges();
    }

    // ---- Map event handlers ----

    onMapReady() {
        if (!this.mapReady) {
            this.mapReady = true;
            this.featuresUpdated$.next(true);
        }
    }

    onFeaturesLoaded() {
        if (!this.isLoaded) {
            this.featuresUpdated$.next(false);
        }
    }

    onFeaturesUpdated(updated: FeatureAndGroup, recordHistory = true) {
        if (this.isAddingFeature) {
            return;
        }

        const isNew = updated.feature.id == null;

        const feature = !isNew
            ? updated
            : {
                  groupId: updated.groupId,
                  feature: {
                      ...updated.feature,
                      id: NEW_FEATURE
                  }
              };

        if (feature?.feature.children != null) {
            feature.feature.children.forEach(c => {
                c.categoryId = feature.feature.categoryId;
            });
        }

        const updateFeature = (existing: MapFeature[], toAdd: MapFeature) =>
            !isNew
                ? existing.map(f => (f.id === toAdd.id ? toAdd : f))
                : existing.concat(toAdd);

        const next = this.internalFeatureGroups.map(g =>
            g.id === updated.groupId
                ? {
                      ...g,
                      existingFeatures: updateFeature(
                          g.existingFeatures,
                          feature.feature
                      )
                  }
                : g
        );
        // Set before refreshValidity, which reads internalFeatureGroups
        this.setInternalFeatureGroups(next);

        this.refreshValidity();

        if (feature.feature.id === NEW_FEATURE) {
            this.map.showFeature(feature);
        }

        this.commitFeatureGroups(next, {
            history: recordHistory ? 'session' : 'none'
        });
    }

    onFeatureSelected(selectedFeature: FeatureAndGroup) {
        if (this.isAddingFeature) {
            return;
        }

        if (
            this.selectedFeature != null &&
            this.selectedFeature.feature.id === selectedFeature.feature.id
        ) {
            this.onFeatureEdit(selectedFeature);
        } else {
            // Opening the popup starts an action: everything done in it undoes
            // back to the state the popup was opened with.
            this.beginSession();
            this.setSelectedFeature(selectedFeature);
        }
    }

    onFeatureUnselected() {
        if (!this.isAddingFeature) {
            this.selectedFeature = null;
            this.selectedFeatureGroup = null;
            // Unselecting during an edit is part of finishing it
            if (!this.isEditing) {
                this.resetSession();
            }
        }
    }

    onFeatureEdit(feature: FeatureAndGroup) {
        this.beginSession();
        this.map.editFeature(
            {
                feature: feature.feature,
                groupId: feature.groupId
            },
            this.findDrawProperties(feature)
        );
        this.setSelectedFeature(feature);
    }

    onFeatureDelete(feature: FeatureAndGroup) {
        const featureGroup = this.internalFeatureGroups.find(
            fg => fg.id === feature.groupId
        );
        this.deleteFeature(featureGroup, feature.feature);
        this.setSelectedFeature(null);
        this.refreshValidity();
        this.map.hidePopup();
    }

    onSelectedFeatureUpdated(updated: FeatureAndGroup) {
        // Read at emit time: the toolbar resets popupView right after emitting,
        // so inside the timeout a preview or rollback would look like an Apply
        const inBufferView = this.markerToolbar?.popupView === 'buffer';
        window.setTimeout(() => {
            if (
                corridorFeatureTypes.includes(
                    <LocationFeatureDto.Type>updated.feature.categoryId
                )
            ) {
                this.handleOffsetFeatureChanged(updated);
            } else if (
                circleFeatureTypes.includes(
                    <LocationFeatureDto.Type>updated.feature.categoryId
                )
            ) {
                this.handleCircleFeatureChanged(updated);
            }

            // This runs in a macrotask, so the selection may have moved on
            // since the emit: the toolbar's buffer rollback emits for the
            // feature the user just LEFT (on feature switch or popup close),
            // not the one selected now. Geometry below is persisted for every
            // emit, but selection sync must only run when the event is about
            // the feature that is still selected - otherwise a rollback would
            // asynchronously re-select the feature the user just clicked away
            // from.
            if (updated.feature.id === this.selectedFeature?.feature?.id) {
                this.setSelectedFeature(updated);
            }
            // Previews and rollbacks are applied but not recorded; the Apply
            // emit arrives after the toolbar has reset, so it is recorded
            this.onFeaturesUpdated(updated, !inBufferView);
            this.changeDetector.detectChanges();
        });
    }

    /**
     * A category switch re-derives the default name.
     * Runs synchronously so the rename is in place before
     * the deferred onFeatureChange handler persists the same object. Manually
     * entered names are never touched here - only names that still match a
     * category's default pattern.
     */
    onSelectedFeatureCategoryChanged(updated: FeatureAndGroup) {
        if (updated.feature.id === this.selectedFeature?.feature?.id) {
            this.setFeatureName(updated);
        }
    }

    onFeatureHover(featureId: number) {
        this.hoverFeatureId = featureId;
    }

    onFeatureNameSelected(feature: MapFeature) {
        if (this.showAirspaceDisclaimer) {
            return;
        }
        const groupId = this.internalFeatureGroups.find(
            g => g.existingFeatures.filter(f => f.id === feature.id).length > 0
        ).id;
        const featureAndGroup: FeatureAndGroup = {
            groupId,
            feature
        };
        this.beginSession();
        this.setSelectedFeature(featureAndGroup);
        this.map.zoomToFeature(feature, true, false);
    }

    onGeocodeResultSelected(feature: MapFeature) {
        this.geocodeResultSelected.emit(feature);
    }

    onMapJurisdictionChanged(jurisdiction: AirspaceJurisdictionDto) {
        this.jurisdictionChanged.emit(jurisdiction);
    }

    onAirspaceValuesUpdated(values: AirspaceCheckerParametersWithStartEnd) {
        this.airspaceValuesUpdated.emit(values);
    }

    onAirspaceDisclaimerAcknowledged() {
        this.airspaceDisclaimerAcknowledged.emit();
    }

    onFullscreenRequested() {
        this.fullscreenService.toggleFullScreen(this.drawingArea);
    }

    onEditModeChanged(isEditingOrEvent: boolean | string) {
        if (isEditingOrEvent === 'cancelled') {
            this.handlePolygonDrawingCancelled();
            return;
        }

        const finishedAdding =
            this.isEditing && this.isAddingFeature && !isEditingOrEvent;

        if (finishedAdding) {
            this._setIsAddingFeature(false);
            this.refreshValidity();
            this.map.showPopup();
            this.setSelectedFeature(this.addedFeature);
            // A drawn feature never reaches onFeaturesUpdated, which ignores
            // updates while adding, so this is where adding is recorded.
            this.commitFeatureGroups(this.internalFeatureGroups, {
                history: 'session'
            });
            // The popup opens on the new feature; whatever is changed there is a
            // step of its own rather than part of adding it.
            this.beginSession();
        }

        if (!isEditingOrEvent) {
            this.validateFeatures();
            if (!finishedAdding) {
                this.resetSession();
            }
        }

        this._setIsEditing(isEditingOrEvent as boolean);
        this.editModeChanged.emit(isEditingOrEvent);
        this.changeDetector.detectChanges();
    }

    handlePolygonDrawingCancelled() {
        if (this.isAddingFeature && this.addedFeature) {
            this.cancelAddingFeature();
            this._setIsAddingFeature(false);
            this.logging.info(
                'Polygon drawing cancelled - at least 3 points are required to create a polygon'
            );
            this.setSelectedFeature(null);
            this._setIsEditing(false);
            // Nothing was committed, so the baseline is simply dropped
            this.resetSession();
            this.changeDetector.detectChanges();
        }
    }

    onViewportChange(viewport: MapViewport) {
        this.viewportChange.emit(viewport);
    }

    // ---- Feature management ----

    addNewFeature(featureGroup: FeatureGroup) {
        this.cancelAddingFeature();

        // Snapshot before the draft is added, so undoing removes the new feature
        this.beginSession();

        this.addingFeatureType = featureGroup.type;
        this._setIsAddingFeature(true);
        const newFeature: MapFeature = {
            id: this.allocateFeatureId(),
            name: this.isExistingFeaturesValid(featureGroup)
                ? numerateNameIfExist(
                      featureGroup.categories[0].name,
                      featureGroup.existingFeatures
                  )
                : featureGroup.categories[0].name,
            categoryId: featureGroup.categories[0].id,
            geom: {
                // @ts-ignore - bad type sensing
                type: featureGroup.type
            }
        };
        this.selectedFeature = {
            groupId: featureGroup.id,
            feature: newFeature
        };
        this.addedFeature = this.selectedFeature;

        this.setInternalFeatureGroups(
            this.internalFeatureGroups.map(g =>
                g.id === featureGroup.id
                    ? {
                          ...g,
                          existingFeatures:
                              g.existingFeatures.concat(newFeature)
                      }
                    : g
            )
        );

        this.map.editFeature({
            groupId: featureGroup.id,
            feature: newFeature
        });
    }

    cancelAddingFeature() {
        if (this.isAddingFeature && this.selectedFeature != null) {
            this.setInternalFeatureGroups(
                this.internalFeatureGroups.map(g =>
                    g.id === this.selectedFeature.groupId
                        ? {
                              ...g,
                              existingFeatures: g.existingFeatures.filter(
                                  f => f.id !== this.selectedFeature.feature.id
                              )
                          }
                        : g
                )
            );
        }
        this.validateFeatures();
    }

    editFeature(featureGroup: FeatureGroup, feature: MapFeature) {
        this.markerToolbar?.revertUnappliedBuffer();
        this.editFeatureId = <number>feature.id;
        this.map.editFeature({
            feature,
            groupId: featureGroup.id
        });
    }

    deleteFeature(featureGroup: FeatureGroup, feature: MapFeature) {
        // The sidebar trash can be clicked while a buffer preview is open on
        // another feature; the baseline must not capture that preview
        this.markerToolbar?.revertUnappliedBuffer();
        this.map.doneEditing();

        if (
            this.pendingConversion != null &&
            feature.id === this.pendingConversion.convertedFeatureId
        ) {
            this.pendingConversion = null;
        }

        // Snapshot after doneEditing, so that an edit it commits stays a step of
        // its own and the delete undoes only the delete.
        const baseline = this.cloneFeatureGroups(this.internalFeatureGroups);
        const wasPresent =
            this.internalFeatureGroups
                .find(fg => fg.id === featureGroup.id)
                ?.existingFeatures.some(f => f.id === feature.id) ?? false;

        const next = this.internalFeatureGroups.map(fg =>
            fg.id !== featureGroup.id
                ? fg
                : {
                      ...fg,
                      existingFeatures: fg.existingFeatures.filter(
                          f => f.id !== feature.id
                      )
                  }
        );
        // Set before validateFeatures, which reads internalFeatureGroups
        this.setInternalFeatureGroups(next);

        this.validateFeatures();
        // A delete that removed nothing is not a step
        this.commitFeatureGroups(
            next,
            wasPresent
                ? { history: 'discrete', discreteBaseline: baseline }
                : { history: 'none' }
        );
        this.resetSession();
    }

    /**
     * Convert a Lines feature into a corridor using default values and move
     * it into the Corridors group. The popup stays open, now showing the
     * corridor's own controls (category radio + line offset).
     */
    onConvertFeatureToCorridor(featureAndGroup: FeatureAndGroup) {
        const geom = featureAndGroup.feature.geom;
        if (
            geom == null ||
            geom.type !== 'LineString' ||
            geom.coordinates.length < 2
        ) {
            this.logging.error(
                null,
                'Unable to convert this feature to a corridor - a line needs at least 2 points'
            );
            return;
        }

        const targetGroup = this.internalFeatureGroups.find(
            g => g.type === 'AreaLineString'
        );
        if (targetGroup == null) {
            return;
        }

        this.map.doneEditing();

        // Snapshot after doneEditing, so that an edit it commits stays a step
        // of its own and undo reverts only the conversion
        const baseline = this.cloneFeatureGroups(this.internalFeatureGroups);

        const built = buildAreaLineFeature(geom, { lineOffsetMeters: 50 });
        const categoryId = targetGroup.categories.some(
            c => c.id === LocationFeatureDto.Type.OFFSET_FLIGHT_AREA
        )
            ? LocationFeatureDto.Type.OFFSET_FLIGHT_AREA
            : targetGroup.categories[0].id;

        const convertedFeature: MapFeature = {
            // Moving between groups requires a fresh id: the old one is only
            // unique within the source group's mapbox source
            id: this.allocateFeatureId(),
            name: featureAndGroup.feature.name,
            categoryId,
            geom: built.geometry,
            properties: built.properties
        };

        this.pendingConversion = {
            sourceGroupId: featureAndGroup.groupId,
            originalFeature: featureAndGroup.feature,
            convertedFeatureId: convertedFeature.id
        };

        this.setInternalFeatureGroups(
            this.internalFeatureGroups.map(g => {
                if (g.id === featureAndGroup.groupId) {
                    return {
                        ...g,
                        existingFeatures: g.existingFeatures.filter(
                            f => f.id !== featureAndGroup.feature.id
                        )
                    };
                }
                if (g.id === targetGroup.id) {
                    return {
                        ...g,
                        existingFeatures:
                            g.existingFeatures.concat(convertedFeature)
                    };
                }
                return g;
            })
        );

        this.setSelectedFeature({
            groupId: targetGroup.id,
            feature: convertedFeature
        });
        this.map.zoomToFeature(convertedFeature, true, false);

        this.validateFeatures();
        this.refreshValidity();
        this.commitFeatureGroups(this.internalFeatureGroups, {
            history: 'discrete',
            discreteBaseline: baseline
        });
        // The popup stays open on the corridor; whatever is changed there is a
        // step of its own rather than part of the conversion
        this.beginSession();
        this.changeDetector.detectChanges();
    }

    /** Whether the currently-selected feature is the corridor produced by a
     * conversion that hasn't been confirmed or reverted yet - drives the
     * Cancel button on the corridor's popup. */
    isPendingConversion(): boolean {
        return (
            this.pendingConversion != null &&
            this.selectedFeature?.feature?.id ===
                this.pendingConversion.convertedFeatureId
        );
    }

    /**
     * Undo a Turn to Corridor conversion while it's still within its revert
     * window: remove the converted corridor and restore the original Line
     * (same id/geom/categoryId) to its source group, mirroring
     * onConvertFeatureToCorridor's group rebuild.
     */
    onCancelConversion(featureAndGroup: FeatureAndGroup) {
        if (this.pendingConversion == null) {
            return;
        }
        const { sourceGroupId, originalFeature, convertedFeatureId } =
            this.pendingConversion;

        // Cancel is an action of its own: undo after it brings the corridor back
        const baseline = this.cloneFeatureGroups(this.internalFeatureGroups);

        this.setInternalFeatureGroups(
            this.internalFeatureGroups.map(g => {
                if (g.type === 'AreaLineString') {
                    return {
                        ...g,
                        existingFeatures: g.existingFeatures.filter(
                            f => f.id !== convertedFeatureId
                        )
                    };
                }
                if (g.id === sourceGroupId) {
                    return {
                        ...g,
                        existingFeatures:
                            g.existingFeatures.concat(originalFeature)
                    };
                }
                return g;
            })
        );

        this.setSelectedFeature({
            groupId: sourceGroupId,
            feature: originalFeature
        });
        this.map.zoomToFeature(originalFeature, true, false);

        this.validateFeatures();
        this.refreshValidity();
        this.commitFeatureGroups(this.internalFeatureGroups, {
            history: 'discrete',
            discreteBaseline: baseline
        });
        this.pendingConversion = null;
        // The popup re-opens on the restored line; changes there are their own step
        this.beginSession();
        this.changeDetector.detectChanges();
    }

    highlightFeature(featureGroup: FeatureGroup, highlightFeatureId: number) {
        this.map.highlightFeature(featureGroup.id, highlightFeatureId);
    }

    unhighlightFeature(featureGroup: FeatureGroup, highlightFeatureId: number) {
        this.map.unhighlightFeature(featureGroup.id, highlightFeatureId);
    }

    // ---- Validity ----

    validateFeatures() {
        this.invalidFeatures.clear();
        this.internalFeatureGroups.forEach(group => {
            if (group.type === 'AreaLineString' || group.type === 'Circle') {
                return;
            }
            group.existingFeatures.forEach(feature => {
                const featureKey = `${group.id}-${feature.id}`;
                if (!this.isFeatureValid(feature)) {
                    this.invalidFeatures.add(featureKey);
                }
            });
        });
        this.changeDetector.detectChanges();
    }

    private isFeatureValid(feature: MapFeature): boolean {
        try {
            if (!feature.geom) {
                return false;
            }
            const geoJsonFeature = {
                type: 'Feature' as const,
                geometry: feature.geom,
                properties: feature.properties || {}
            };
            return booleanValid(geoJsonFeature);
        } catch (error) {
            return false;
        }
    }

    isFeatureInvalid(groupId: number, featureId: number | string): boolean {
        return this.invalidFeatures.has(`${groupId}-${featureId}`);
    }

    hasInvalidFeatures(): boolean {
        return this.invalidFeatures.size > 0;
    }

    private refreshValidity() {
        const mergeAcc = (
            a: { hasAnyFeatures: boolean; hasRequiredFeatures: boolean },
            b: { hasAnyFeatures: boolean; hasRequiredFeatures: boolean }
        ) => ({
            hasAnyFeatures: a.hasAnyFeatures || b.hasAnyFeatures,
            hasRequiredFeatures: a.hasRequiredFeatures || b.hasRequiredFeatures
        });

        const { hasAnyFeatures, hasRequiredFeatures } =
            this.internalFeatureGroups.reduce(
                (acc, fg) =>
                    mergeAcc(
                        acc,
                        fg.existingFeatures.reduce(
                            (acc2, f) => ({
                                hasAnyFeatures: true,
                                hasRequiredFeatures:
                                    acc2.hasRequiredFeatures ||
                                    this.requiredFeatureTypes.includes(
                                        f.categoryId
                                    ) ||
                                    this.requiredFeatureTypes.length === 0
                            }),
                            {
                                hasAnyFeatures: false,
                                hasRequiredFeatures: false
                            } as {
                                hasAnyFeatures: boolean;
                                hasRequiredFeatures: boolean;
                            }
                        )
                    ),
                { hasAnyFeatures: false, hasRequiredFeatures: false }
            );

        this.hasRequiredFeaturesChange.emit(hasRequiredFeatures);
        this.hasAnyFeaturesChange.emit(hasAnyFeatures);

        const flightArea = pipe(
            flightAreaOfFeatures(this.getLocationFeatures()),
            map<any, GeoJSON.Polygon>(f =>
                f.geometry.type !== 'GeometryCollection'
                    ? <GeoJSON.Polygon>f.geometry
                    : <GeoJSON.Polygon>(
                          (<GeometryCollection>f.geometry).geometries.find(
                              g => g.type === 'Polygon'
                          )
                      )
            ),
            getOrElse(() => null as GeoJSON.Polygon)
        );

        this.flightArea = flightArea;
        this.flightAreaChange.emit(flightArea);
    }

    /** Extract location-feature-like objects from internal feature groups for helpers. */
    private getLocationFeatures(): any[] {
        return this.internalFeatureGroups.reduce(
            (acc: any[], fg) =>
                acc.concat(
                    fg.existingFeatures.map(f => ({
                        name: f.name,
                        geometry: f.geom,
                        type: f.categoryId,
                        properties: f.properties
                    }))
                ),
            []
        );
    }

    // ---- Undo history helpers ----

    /**
     * Applies the new feature groups, emits them to the parent, and records the
     * step in the undo history. Every edit goes through here so that recording
     * and the echo guard can't get out of step.
     *
     * `session` folds the change into the action in progress: the first commit
     * of the session records the step and later ones join it. `discrete` records
     * `discreteBaseline` as a step of its own, for one off changes like a delete.
     * `none` applies and emits without recording, for the popup's buffer
     * previews and rollbacks, which are not committed changes.
     */
    private commitFeatureGroups(
        next: FeatureGroup[],
        options: {
            history: 'session' | 'discrete' | 'none';
            discreteBaseline?: FeatureGroup[];
        }
    ) {
        if (options.history === 'session') {
            this.recordSessionStep();
        } else if (
            options.history === 'discrete' &&
            options.discreteBaseline != null
        ) {
            this.pushSnapshot(options.discreteBaseline);
        }

        this.setInternalFeatureGroups(next);
        this._lastEmittedFeatureGroups = next;
        this.featureGroupsChange.emit(next);
    }

    /**
     * Take the snapshot for an action that is about to start. This has to happen
     * before the action touches anything, because the map and the popup edit
     * features in place, which would otherwise alter the snapshot after the fact.
     *
     * Any un-applied buffer preview is reverted first: the snapshot must not
     * contain geometry the user never applied, and when the action is an edit
     * the draw layer takes a synchronous copy of feature.geom right after this,
     * which has to be the original geometry too. The toolbar's own rollback on
     * feature switch then finds nothing left to revert.
     */
    private beginSession() {
        this.markerToolbar?.revertUnappliedBuffer();
        this.sessionBaseline = this.cloneFeatureGroups(
            this.internalFeatureGroups
        );
        this.sessionPushed = false;
    }

    private resetSession() {
        this.sessionBaseline = null;
        this.sessionPushed = false;
    }

    /** Push the session baseline, but only for the first change of the session. */
    private recordSessionStep() {
        if (this.sessionBaseline == null || this.sessionPushed) {
            return;
        }
        this.pushSnapshot(this.sessionBaseline);
        this.sessionPushed = true;
    }

    /** Record a manual action. It invalidates whatever was undone before it. */
    private pushSnapshot(snapshot: FeatureGroup[]) {
        this.pushUndo(snapshot);
        this.redoStack = [];
    }

    private pushUndo(snapshot: FeatureGroup[]) {
        this.undoStack.push(snapshot);
        if (this.undoStack.length > this.maxUndoDepth) {
            this.undoStack.shift();
        }
    }

    private cloneFeatureGroups(featureGroups: FeatureGroup[]): FeatureGroup[] {
        return structuredClone(featureGroups);
    }

    // ---- Private helpers ----

    /** Assigns internalFeatureGroups and rebuilds the derived featureNameTooltips
     * lookup. */
    private setInternalFeatureGroups(featureGroups: FeatureGroup[]) {
        this.internalFeatureGroups = featureGroups;
        this.refreshFeatureNameTooltips();
    }

    private refreshFeatureNameTooltips() {
        const tooltips: { [key: string]: string } = {};
        this.internalFeatureGroups.forEach(featureGroup => {
            featureGroup.existingFeatures.forEach(feature => {
                tooltips[`${featureGroup.id}-${feature.id}`] =
                    this.buildFeatureNameTooltip(featureGroup, feature);
            });
        });
        this.featureNameTooltips = tooltips;
    }

    /** Builds the feature name tooltip text, appending the feature's category when known. */
    private buildFeatureNameTooltip(
        featureGroup: FeatureGroup,
        feature: MapFeature
    ): string {
        const category = featureGroup?.categories?.find(
            c => c.id === feature.categoryId
        );
        return category != null
            ? `${feature.name} - ${category.name}`
            : feature.name;
    }

    private setSelectedFeature(feature: FeatureAndGroup) {
        // The conversion revert window only closes when the user selects a
        // DIFFERENT feature. Mere deselection keeps it open, in both forms:
        // passing null here (e.g. after a cancelled draw), and clicking empty
        // map space (which goes through onFeatureUnselected and never reaches
        // this method at all). Re-selecting the corridor then still offers
        // Cancel.
        if (
            feature != null &&
            this.pendingConversion != null &&
            feature.feature.id !== this.pendingConversion.convertedFeatureId
        ) {
            this.pendingConversion = null;
        }
        this.selectedFeature = feature;
        this.selectedFeatureGroup = feature
            ? this.internalFeatureGroups.find(fg => fg.id === feature.groupId)
            : null;
    }

    private setFeatureName(feature: FeatureAndGroup) {
        if (!!feature && this.isSelectedFeatureGroupValid()) {
            const categories = this.selectedFeatureGroup.categories;
            const presetCategoryNames = categories.map(c => c.name);
            const isFeatureNameDefault =
                presetCategoryNames.includes(feature.feature.name) ||
                presetCategoryNames.some(presetCategoryName =>
                    new RegExp(`^${presetCategoryName}( \\d+)?$`).test(
                        feature.feature.name
                    )
                );
            const featureGroup = this.internalFeatureGroups.find(
                fg => fg.id === feature.groupId
            );
            if (isFeatureNameDefault) {
                const newName = categories.find(
                    category => category.id === feature.feature.categoryId
                ).name;
                feature.feature.name =
                    this.isExistingFeaturesValid(featureGroup) &&
                    featureGroup.existingFeatures.filter(
                        ef => ef.id !== feature.feature.id
                    ).length > 0
                        ? numerateNameIfExist(
                              newName,
                              featureGroup.existingFeatures.filter(
                                  ef => ef.id !== feature.feature.id
                              )
                          )
                        : newName;
            }
        }
    }

    isSelectedFeatureGroupValid() {
        return (
            !!this.selectedFeatureGroup &&
            !!this.selectedFeatureGroup.categories &&
            Array.isArray(this.selectedFeatureGroup.categories) &&
            this.selectedFeatureGroup.categories.length !== 0
        );
    }

    isExistingFeaturesValid(featureGroup: FeatureGroup | null) {
        return (
            featureGroup != null && featureGroup.existingFeatures.length !== 0
        );
    }

    private findDrawProperties(feature: FeatureAndGroup): GeoJsonProperties {
        const group = this.internalFeatureGroups.find(
            g => g.id === feature.groupId
        );
        if (group == null) {
            return undefined;
        }

        if (group.type !== 'Point' || feature.feature.categoryId == null) {
            return undefined;
        }

        const style = group.styles.symbol.find(
            s =>
                s.layout?.['icon-image'] != null &&
                s.filter[0] === '==' &&
                s.filter[2] === feature.feature.categoryId
        );

        return style.layout;
    }

    private handleOffsetFeatureChanged(featureAndGroup: FeatureAndGroup) {
        const linestring = (<GeometryCollection>(
            featureAndGroup.feature.geom
        )).geometries.find(g => g.type === 'LineString');
        const updatedFeature = buildAreaLineFeature(
            <LineString>linestring,
            featureAndGroup.feature.properties
        );
        featureAndGroup.feature.geom = updatedFeature.geometry;
        featureAndGroup.feature.properties = updatedFeature.properties;
    }

    private handleCircleFeatureChanged(featureAndGroup: FeatureAndGroup) {
        const geom = featureAndGroup.feature.geom;
        if (geom.type !== 'GeometryCollection') {
            return;
        }
        const point = geom.geometries.find(g => g.type === 'Point');
        if (point == null) {
            return;
        }
        const updatedFeature = buildCircleFeature(
            <Point>point,
            featureAndGroup.feature.properties
        );
        featureAndGroup.feature.geom = updatedFeature.geometry;
        featureAndGroup.feature.properties = updatedFeature.properties;
    }

    /**
     * Allocate an id that cannot collide with any existing feature, in any
     * group. Recomputed on each allocation rather than kept in a counter, so
     * that it stays correct however the parent numbered the features it
     * loaded, and whatever gaps that numbering left behind.
     */
    private allocateFeatureId(): number {
        return (
            this.internalFeatureGroups.reduce(
                (acc, group) =>
                    group.existingFeatures.reduce(
                        (a, f) =>
                            typeof f.id === 'number' ? Math.max(a, f.id) : a,
                        acc
                    ),
                0
            ) + 1
        );
    }

    private _setIsAddingFeature(value: boolean) {
        this.isAddingFeature = value;
        if (!value) {
            this.addingFeatureType = null;
        }
        this.isAddingFeatureChange.emit(value);
    }

    private _setIsEditing(value: boolean) {
        this.isEditing = value;
        this.isEditingChange.emit(value);
    }
}
